import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  ClickAwayListener,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Iconify from "../iconify";
import {
  useGetMembersQuery,
  useSendRegistrationLinksMutation,
} from "../../features/members/membersApiSlice";

const LARGE_SEND_THRESHOLD = 200;
const DEFAULT_LINK_VALIDITY_HOURS = 24;
const MAX_LINK_VALIDITY_HOURS = 24 * 365;

const normalizeStatus = (value) => String(value || "").toLowerCase();

const getMemberLabel = (member) => {
  const fullName = `${member?.firstName || ""} ${member?.lastName || ""}`.trim();
  return fullName || member?.memberNumber || "Unbekanntes Mitglied";
};

const getSiteId = (member) =>
  String(member?.standortRef?._id || member?.standortRef || "");

const getSiteName = (member) => member?.standortRef?.siteName || "Ohne Standort";

const getResultStatusLabel = (status) => {
  if (status === "sent") return "Versendet";
  if (status === "failed") return "Fehlgeschlagen";
  if (status === "skipped") return "Übersprungen";
  return "Unbekannt";
};

const getResultStatusColor = (status) => {
  if (status === "sent") return "success";
  if (status === "failed") return "error";
  if (status === "skipped") return "warning";
  return "default";
};

const formatDuration = (value) => {
  const durationMs = Number(value || 0);
  if (!durationMs) return "";
  if (durationMs < 1000) return `${durationMs} ms`;
  const seconds = durationMs / 1000;
  return `${seconds >= 10 ? seconds.toFixed(0) : seconds.toFixed(1)} Sek.`;
};

const csvEscape = (value) =>
  `"${String(value || "")
    .replace(/"/g, '""')
    .replace(/\r?\n/g, " ")}"`;

const getLinkValidityHoursLabel = (hours) => {
  const normalizedHours = Math.max(
    1,
    Number.parseInt(hours, 10) || DEFAULT_LINK_VALIDITY_HOURS,
  );
  return normalizedHours === 1
    ? "1 Stunde"
    : `${normalizedHours} Stunden`;
};

const getLinkValidityText = ({ hours, neverExpires }) =>
  neverExpires
    ? "Die Links sind unbegrenzt gültig und verfallen nicht automatisch."
    : `Die Links sind ${getLinkValidityHoursLabel(hours)} gültig.`;

function MemberRegistrationLinksDialog({ handleClose }) {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const reportSectionRef = useRef(null);
  const { data: membersData, isLoading } = useGetMembersQuery();
  const [sendRegistrationLinks, { isLoading: isSending }] =
    useSendRegistrationLinksMutation();

  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [selectionError, setSelectionError] = useState("");
  const [sendError, setSendError] = useState("");
  const [sendReport, setSendReport] = useState(null);
  const [resultFilter, setResultFilter] = useState("all");
  const [resultSearch, setResultSearch] = useState("");
  const [isMemberAutocompleteOpen, setIsMemberAutocompleteOpen] = useState(false);
  const [linkValidityHours, setLinkValidityHours] = useState(
    String(DEFAULT_LINK_VALIDITY_HOURS),
  );
  const [linkNeverExpires, setLinkNeverExpires] = useState(false);
  const [linkValidityError, setLinkValidityError] = useState("");

  const activeMembers = useMemo(() => {
    const members = Array.isArray(membersData) ? membersData : [];
    return members.filter((member) => normalizeStatus(member?.status) === "active");
  }, [membersData]);

  const activeMemberIdSet = useMemo(
    () => new Set(activeMembers.map((member) => String(member?._id))),
    [activeMembers],
  );

  useEffect(() => {
    setSelectedMemberIds((prev) =>
      prev.filter((memberId) => activeMemberIdSet.has(String(memberId))),
    );
  }, [activeMemberIdSet]);

  useEffect(() => {
    if ((sendReport || sendError) && reportSectionRef.current) {
      reportSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [sendReport, sendError]);

  const membersById = useMemo(
    () => new Map(activeMembers.map((member) => [String(member._id), member])),
    [activeMembers],
  );

  const selectedMembers = useMemo(
    () =>
      selectedMemberIds
        .map((memberId) => membersById.get(String(memberId)))
        .filter(Boolean),
    [membersById, selectedMemberIds],
  );

  const siteOptions = useMemo(() => {
    const map = new Map();

    activeMembers.forEach((member) => {
      const siteId = getSiteId(member);
      if (!siteId) return;

      if (!map.has(siteId)) {
        map.set(siteId, {
          _id: siteId,
          siteName: getSiteName(member),
          memberIds: [],
        });
      }

      map.get(siteId).memberIds.push(String(member._id));
    });

    return [...map.values()]
      .map((site) => ({ ...site, count: site.memberIds.length }))
      .sort((a, b) => a.siteName.localeCompare(b.siteName));
  }, [activeMembers]);

  const selectedSites = useMemo(() => {
    const selectedSiteIdSet = new Set(selectedSiteIds.map((id) => String(id)));
    return siteOptions.filter((site) => selectedSiteIdSet.has(String(site._id)));
  }, [selectedSiteIds, siteOptions]);

  const addMembersByIds = (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return;

    setSelectedMemberIds((prev) => {
      const next = new Set(prev.map((id) => String(id)));
      ids.forEach((id) => {
        const memberId = String(id);
        if (activeMemberIdSet.has(memberId)) {
          next.add(memberId);
        }
      });
      return [...next];
    });
  };

  const estimatedRecipients = useMemo(() => {
    const recipients = selectedMembers
      .map((member) => String(member?.contractRef?.email || "").trim().toLowerCase())
      .filter(Boolean);
    return new Set(recipients).size;
  }, [selectedMembers]);

  const selectedWithoutEmailCount = useMemo(
    () =>
      selectedMembers.filter(
        (member) => !String(member?.contractRef?.email || "").trim(),
      ).length,
    [selectedMembers],
  );

  const duplicateEmailCount = useMemo(() => {
    const allRecipients = selectedMembers
      .map((member) => String(member?.contractRef?.email || "").trim().toLowerCase())
      .filter(Boolean);
    return allRecipients.length - estimatedRecipients;
  }, [estimatedRecipients, selectedMembers]);

  const reportResults = useMemo(
    () => (Array.isArray(sendReport?.results) ? sendReport.results : []),
    [sendReport],
  );

  const failedMemberIds = useMemo(() => {
    const ids = new Set();
    reportResults.forEach((item) => {
      if (normalizeStatus(item?.status) !== "failed") return;
      const memberId = String(item?.memberId || "").trim();
      if (!memberId) return;
      ids.add(memberId);
    });
    return [...ids];
  }, [reportResults]);

  const reportCounts = useMemo(() => {
    const counts = {
      all: reportResults.length,
      sent: 0,
      skipped: 0,
      failed: 0,
    };
    reportResults.forEach((item) => {
      const status = String(item?.status || "").toLowerCase();
      if (counts[status] !== undefined) {
        counts[status] += 1;
      }
    });
    return counts;
  }, [reportResults]);

  const filteredReportResults = useMemo(() => {
    const query = resultSearch.trim().toLowerCase();

    return reportResults.filter((item) => {
      if (resultFilter !== "all" && item?.status !== resultFilter) {
        return false;
      }

      if (!query) return true;

      const haystack = [
        item?.memberName,
        item?.recipient,
        item?.message,
        item?.memberId,
      ]
        .map((value) => String(value || "").toLowerCase())
        .join(" ");

      return haystack.includes(query);
    });
  }, [reportResults, resultFilter, resultSearch]);

  const reportSummarySeverity = useMemo(() => {
    const failedCount = Number(sendReport?.failedCount || 0);
    const sentCount = Number(sendReport?.sentCount || 0);
    if (failedCount > 0) return "warning";
    if (sentCount > 0) return "success";
    return "error";
  }, [sendReport]);

  const parsedLinkValidityHours = useMemo(() => {
    const trimmedValue = String(linkValidityHours || "").trim();
    if (!trimmedValue) return null;
    const parsedValue = Number(trimmedValue);
    if (!Number.isInteger(parsedValue) || parsedValue < 1) return null;
    return parsedValue;
  }, [linkValidityHours]);

  const currentLinkValidityText = useMemo(
    () =>
      getLinkValidityText({
        hours: parsedLinkValidityHours || DEFAULT_LINK_VALIDITY_HOURS,
        neverExpires: linkNeverExpires,
      }),
    [linkNeverExpires, parsedLinkValidityHours],
  );

  const resolveLinkValidityPayload = () => {
    if (linkNeverExpires) {
      setLinkValidityError("");
      return { neverExpires: true, validityHours: null };
    }

    const trimmedValue = String(linkValidityHours || "").trim();
    if (!trimmedValue) {
      setLinkValidityError("Bitte eine Stundenanzahl eingeben.");
      return null;
    }

    const parsedValue = Number(trimmedValue);
    if (
      !Number.isInteger(parsedValue) ||
      parsedValue < 1 ||
      parsedValue > MAX_LINK_VALIDITY_HOURS
    ) {
      setLinkValidityError(
        `Bitte eine ganze Zahl zwischen 1 und ${MAX_LINK_VALIDITY_HOURS} eingeben.`,
      );
      return null;
    }

    setLinkValidityError("");
    return { neverExpires: false, validityHours: parsedValue };
  };

  const handleExportReport = () => {
    if (!reportResults.length) return;

    const rows = [
      ["Status", "Mitglied", "E-Mail", "Hinweis", "Mitglieds-ID"],
      ...reportResults.map((item) => [
        getResultStatusLabel(item?.status),
        item?.memberName || "",
        item?.recipient || "",
        item?.message || "",
        item?.memberId || "",
      ]),
    ];

    const csv = rows.map((row) => row.map(csvEscape).join(";")).join("\n");
    const blob = new Blob([`\uFEFF${csv}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `registrierungslink-versand-${new Date()
      .toISOString()
      .replace(/[:.]/g, "-")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const runSend = async (memberIds) => {
    const ids = [
      ...new Set(
        (memberIds || [])
          .map((id) => String(id || "").trim())
          .filter(Boolean),
      ),
    ];
    if (!ids.length) return;

    const validityPayload = resolveLinkValidityPayload();
    if (!validityPayload) return;

    setSelectionError("");
    setSendError("");
    setSendReport(null);

    try {
      const response = await sendRegistrationLinks({
        memberIds: ids,
        ...validityPayload,
      }).unwrap();

      const failedCount = Number(response?.failedCount || 0);
      const skippedCount = Number(response?.skippedCount || 0);

      setSendReport(response);
      setResultSearch("");
      setResultFilter(
        failedCount > 0 ? "failed" : skippedCount > 0 ? "skipped" : "all",
      );
    } catch (error) {
      setSendError(
        error?.data?.message ||
          "Registrierungslinks konnten nicht versendet werden.",
      );
    }
  };

  const handleSend = async () => {
    if (!selectedMemberIds.length) {
      setSelectionError("Bitte mindestens ein aktives Mitglied auswählen.");
      return;
    }
    await runSend(selectedMemberIds);
  };

  const handleRetryFailed = async () => {
    if (!failedMemberIds.length) return;
    await runSend(failedMemberIds);
  };

  let sendButtonLabel = `${selectedMemberIds.length} Registrierungslink(s) senden`;

  if (isMobile) {
    sendButtonLabel = `${selectedMemberIds.length} ${
      selectedMemberIds.length === 1 ? "Link" : "Links"
    } senden`;
  }

  if (isSending) {
    sendButtonLabel = "Senden...";
  }

  return (
    <Dialog
      open
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          background: "linear-gradient(180deg, #f6f8fb 0%, #ffffff 42%)",
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          background:
            "linear-gradient(120deg, #0f172a 0%, #1e293b 58%, #0f766e 100%)",
          color: "common.white",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              backgroundColor: "rgba(255, 255, 255, 0.16)",
              display: "grid",
              placeItems: "center",
            }}
          >
            <Iconify icon="mdi:email-fast-outline" width={24} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{
                color: "common.white",
                fontSize: {
                  xs: muiTheme.typography.h5.fontSize,
                  sm: muiTheme.typography.h4.fontSize,
                },
                lineHeight: 1.2,
              }}
            >
              Registrierungslink senden
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.86, lineHeight: 1.45 }}>
              Schrittweise Auswahl mit sofortiger Versandübersicht.
            </Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent sx={{ pt: 3, px: { xs: 2, sm: 3 } }}>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            backgroundColor: "rgba(15, 118, 110, 0.04)",
            borderColor: "rgba(15, 118, 110, 0.22)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            divider={
              <Divider
                flexItem
                orientation="vertical"
                sx={{ display: { xs: "none", md: "block" } }}
              />
            }
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Iconify icon="mdi:numeric-1-circle-outline" width={18} />
              <Typography variant="body2">Mitglieder auswählen</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Iconify icon="mdi:numeric-2-circle-outline" width={18} />
              <Typography variant="body2">Links senden</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Iconify icon="mdi:numeric-3-circle-outline" width={18} />
              <Typography variant="body2">Ergebnis prüfen / exportieren</Typography>
            </Stack>
          </Stack>
        </Paper>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Chip
            icon={<Iconify icon="mdi:account-multiple" width={16} />}
            label={`${selectedMemberIds.length} ausgewählt`}
            color="primary"
            variant="outlined"
          />
          <Chip
            icon={<Iconify icon="mdi:office-building-marker-outline" width={16} />}
            label={`${selectedSiteIds.length} Standort(e)`}
            color="default"
            variant="outlined"
          />
          <Chip
            icon={<Iconify icon="mdi:email-outline" width={16} />}
            label={`${estimatedRecipients} mögliche Empfänger`}
            color="default"
            variant="outlined"
          />
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          Jeder Link ist individuell. {currentLinkValidityText} Mitglieder legen
          ihr Passwort danach selbst fest.
        </Alert>

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            borderColor: "divider",
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Link-Gültigkeit
          </Typography>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              type="number"
              label="Gültig für"
              value={linkValidityHours}
              disabled={linkNeverExpires || isSending}
              onChange={(event) => {
                setLinkValidityHours(event.target.value);
                setLinkValidityError("");
              }}
              error={Boolean(linkValidityError) && !linkNeverExpires}
              helperText={
                linkNeverExpires
                  ? "Bei unbegrenzter Gültigkeit wird keine Stundenanzahl benötigt."
                  : linkValidityError ||
                    `Standard: ${DEFAULT_LINK_VALIDITY_HOURS} Stunden. Maximal ${MAX_LINK_VALIDITY_HOURS} Stunden.`
              }
              sx={{ width: { xs: "100%", md: 220 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">Std.</InputAdornment>
                ),
                inputProps: {
                  min: 1,
                  max: MAX_LINK_VALIDITY_HOURS,
                  step: 1,
                },
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={linkNeverExpires}
                  onChange={(event) => {
                    setLinkNeverExpires(event.target.checked);
                    setLinkValidityError("");
                  }}
                  disabled={isSending}
                />
              }
              label="Link ist unbegrenzt gültig"
            />
          </Stack>

          {linkNeverExpires ? (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              Unbegrenzte Links verfallen nicht automatisch. Verwenden Sie diese
              Option nur, wenn das bewusst gewünscht ist.
            </Alert>
          ) : null}
        </Paper>

        {selectedWithoutEmailCount > 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {selectedWithoutEmailCount} ausgewählte Mitglied(er) haben keine
            Vertrags-E-Mail und werden automatisch übersprungen.
          </Alert>
        ) : null}

        {duplicateEmailCount > 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {duplicateEmailCount} Mitglied(er) teilen sich eine E-Mail-Adresse.
            Die Übersicht zeigt trotzdem den Status pro Mitglied.
          </Alert>
        ) : null}

        {selectedMemberIds.length >= LARGE_SEND_THRESHOLD ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Großer Versand ({selectedMemberIds.length} Mitglieder): Der Versand
            erfolgt serverseitig in sicheren Wellen. Bitte Dialog bis zum
            Abschluss geöffnet lassen.
          </Alert>
        ) : null}

        {isLoading ? (
          <Box
            sx={{
              minHeight: 220,
              display: "grid",
              placeItems: "center",
            }}
          >
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Paper
                variant="outlined"
                sx={{ p: 2, borderRadius: 2, borderColor: "divider" }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Standorte
                </Typography>
                <Autocomplete
                  multiple
                  options={siteOptions}
                  value={selectedSites}
                  isOptionEqualToValue={(option, value) =>
                    String(option._id) === String(value._id)
                  }
                  getOptionLabel={(option) =>
                    `${option.siteName} (${option.count})`
                  }
                  onChange={(_, nextValue) => {
                    setSelectedSiteIds(nextValue.map((site) => String(site._id)));
                    setSelectionError("");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Standort wählen"
                      placeholder="Standort"
                    />
                  )}
                  sx={{ mb: 1.5 }}
                />
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    disabled={selectedSites.length === 0}
                    onClick={() => {
                      addMembersByIds(
                        selectedSites.flatMap((site) => site.memberIds || []),
                      );
                    }}
                  >
                    Standort hinzufügen
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setSelectedMemberIds(
                        activeMembers.map((member) => String(member._id)),
                      );
                      setSelectionError("");
                    }}
                  >
                    Alle aktiv
                  </Button>
                </Stack>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {siteOptions.map((site) => (
                    <Chip
                      key={site._id}
                      label={`${site.siteName} (${site.count})`}
                      variant="outlined"
                      onClick={() => addMembersByIds(site.memberIds)}
                      icon={
                        <Iconify icon="mdi:plus-circle-outline" width={16} />
                      }
                    />
                  ))}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={7}>
              <Paper
                variant="outlined"
                sx={{ p: 2, borderRadius: 2, borderColor: "divider" }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Mitgliederauswahl
                </Typography>
                <ClickAwayListener
                  onClickAway={() => setIsMemberAutocompleteOpen(false)}
                >
                  <Box>
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      open={isMemberAutocompleteOpen}
                      onOpen={() => setIsMemberAutocompleteOpen(true)}
                      onClose={(_, reason) => {
                        if (
                          reason !== "selectOption" &&
                          reason !== "removeOption"
                        ) {
                          setIsMemberAutocompleteOpen(false);
                        }
                      }}
                      options={activeMembers}
                      value={selectedMembers}
                      isOptionEqualToValue={(option, value) =>
                        String(option?._id) === String(value?._id)
                      }
                      groupBy={(option) => getSiteName(option)}
                      getOptionLabel={(option) => getMemberLabel(option)}
                      onChange={(_, nextValue) => {
                        setSelectedMemberIds(
                          nextValue.map((member) => String(member._id)),
                        );
                        setSelectionError("");
                      }}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox checked={selected} sx={{ mr: 1 }} />
                          <Box sx={{ width: "100%" }}>
                            <Typography variant="body2">
                              {getMemberLabel(option)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option?.contractRef?.email
                                ? option.contractRef.email
                                : "Keine E-Mail im Vertrag"}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Aktive Mitglieder"
                          placeholder="Mitglieder suchen"
                          onClick={() => setIsMemberAutocompleteOpen(true)}
                        />
                      )}
                    />
                  </Box>
                </ClickAwayListener>

                {selectionError ? (
                  <Alert severity="error" sx={{ mt: 1.5 }}>
                    {selectionError}
                  </Alert>
                ) : null}
              </Paper>
            </Grid>
          </Grid>
        )}

        {isSending ? (
          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              borderColor: "secondary.light",
              backgroundColor: "rgba(14, 116, 144, 0.05)",
            }}
          >
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                Versand läuft. Bitte warten...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Für große Mengen kann der Versand etwas dauern. Die vollständige
                Übersicht wird automatisch nach Abschluss angezeigt.
              </Typography>
              <LinearProgress color="secondary" />
            </Stack>
          </Paper>
        ) : null}

        {sendError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {sendError}
          </Alert>
        ) : null}

        {sendReport ? (
          <Paper
            ref={reportSectionRef}
            variant="outlined"
            sx={{ mt: 2, p: 2, borderRadius: 2, borderColor: "divider" }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              sx={{ mb: 1.5 }}
            >
              <Box>
                <Typography variant="h6">Versandübersicht</Typography>
                <Typography variant="body2" color="text.secondary">
                  Letzter Versand: {reportCounts.all} Eintrag/Einträge
                  {sendReport?.durationMs
                    ? ` in ${formatDuration(sendReport.durationMs)}`
                    : ""}
                  .
                </Typography>
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Iconify icon="mdi:refresh" width={18} />}
                  onClick={handleRetryFailed}
                  disabled={isSending || failedMemberIds.length === 0}
                >
                  Nur Fehler erneut senden ({failedMemberIds.length})
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<Iconify icon="mdi:file-delimited-outline" width={18} />}
                  onClick={handleExportReport}
                  disabled={reportCounts.all === 0}
                >
                  CSV exportieren
                </Button>
              </Stack>
            </Stack>

            <Alert severity={reportSummarySeverity} sx={{ mb: 1.5 }}>
              {Number(sendReport?.sentCount || 0)} versendet,{" "}
              {Number(sendReport?.skippedCount || 0)} übersprungen,{" "}
              {Number(sendReport?.failedCount || 0)} fehlgeschlagen.
              {sendReport?.validity?.label
                ? ` Link-Gültigkeit: ${sendReport.validity.label}.`
                : ""}
              {sendReport?.processing?.concurrency
                ? ` Verarbeitung parallelisiert (${sendReport.processing.concurrency} gleichzeitig).`
                : ""}
            </Alert>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ mb: 1.5 }}
            >
              <Chip
                size="small"
                color="default"
                variant="outlined"
                label={`Gesamt: ${reportCounts.all}`}
              />
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={`Versendet: ${reportCounts.sent}`}
              />
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={`Übersprungen: ${reportCounts.skipped}`}
              />
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label={`Fehlgeschlagen: ${reportCounts.failed}`}
              />
            </Stack>

            <Tabs
              value={resultFilter}
              onChange={(_, value) => setResultFilter(value)}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{ mb: 1 }}
            >
              <Tab label={`Alle (${reportCounts.all})`} value="all" />
              <Tab label={`Versendet (${reportCounts.sent})`} value="sent" />
              <Tab
                label={`Übersprungen (${reportCounts.skipped})`}
                value="skipped"
              />
              <Tab
                label={`Fehlgeschlagen (${reportCounts.failed})`}
                value="failed"
              />
            </Tabs>

            <TextField
              fullWidth
              size="small"
              placeholder="Ergebnisse durchsuchen (Name, E-Mail, Hinweis, ID)"
              value={resultSearch}
              onChange={(event) => setResultSearch(event.target.value)}
              sx={{ mb: 1.5 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="mdi:magnify" width={18} />
                  </InputAdornment>
                ),
              }}
            />

            <TableContainer
              sx={{
                maxHeight: 320,
                borderRadius: 1.5,
                border: (tableTheme) => `1px solid ${tableTheme.palette.divider}`,
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Mitglied</TableCell>
                    <TableCell>E-Mail</TableCell>
                    <TableCell>Hinweis</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredReportResults.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ py: 1 }}
                        >
                          Keine Einträge für den gewählten Filter.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredReportResults.map((item, index) => (
                      <TableRow
                        key={`${item?.memberId || "member"}-${index}`}
                        hover
                      >
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Chip
                            size="small"
                            label={getResultStatusLabel(item?.status)}
                            color={getResultStatusColor(item?.status)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {item?.memberName || "Unbekanntes Mitglied"}
                        </TableCell>
                        <TableCell>{item?.recipient || "-"}</TableCell>
                        <TableCell>{item?.message || "-"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : null}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 2.5 },
          pt: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1,
          "& > :not(style) ~ :not(style)": {
            ml: 0,
          },
        }}
      >
        <Button
          onClick={() => {
            setSelectedMemberIds([]);
            setSelectedSiteIds([]);
            setSelectionError("");
          }}
          color="inherit"
          disabled={isSending}
          sx={{
            width: { xs: "100%", sm: "auto" },
            order: { xs: 3, sm: 1 },
          }}
        >
          Auswahl leeren
        </Button>
        <Button
          onClick={handleClose}
          color="inherit"
          disabled={isSending}
          sx={{
            width: { xs: "100%", sm: "auto" },
            order: { xs: 2, sm: 2 },
          }}
        >
          Schließen
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          color="secondary"
          fullWidth={isMobile}
          disabled={isSending || isLoading || selectedMemberIds.length === 0}
          startIcon={
            isSending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <Iconify icon="mdi:send-check-outline" width={18} />
            )
          }
          sx={{
            order: { xs: 1, sm: 3 },
            whiteSpace: { xs: "normal", sm: "nowrap" },
            textAlign: "center",
            lineHeight: 1.35,
            minHeight: 44,
          }}
        >
          {sendButtonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

MemberRegistrationLinksDialog.propTypes = {
  handleClose: PropTypes.func.isRequired,
};

export default MemberRegistrationLinksDialog;
